import { useApp } from "../../state/store";
import { useT } from "../../i18n/store";
import { PAPER_PRESETS } from "../../domain/presets";
import type { Orientation, PaperPreset } from "../../domain/types";
import { Section } from "../Section";
import { NumberInput } from "../NumberInput";
import { Segmented } from "../Segmented";

const ALL_PRESETS: PaperPreset[] = ["A0", "A1", "A2", "A3", "A4", "A5", "A6", "LETTER", "LEGAL", "CUSTOM"];

export const PaperSection = () => {
  const t = useT();
  const paper = useApp((s) => s.paper);
  const setPaper = useApp((s) => s.setPaper);

  const onPreset = (v: PaperPreset) => {
    if (v === "CUSTOM") {
      setPaper({ preset: v });
      return;
    }
    const [w, h] = PAPER_PRESETS[v];
    let width = w, height = h;
    if (paper.orientation === "landscape" && width < height) {
      [width, height] = [height, width];
    }
    setPaper({ preset: v, width, height });
  };

  const setOrientation = (o: Orientation) => {
    if (o === "auto") {
      setPaper({ orientation: o });
      return;
    }
    const wantsLandscape = o === "landscape";
    const isLandscape = paper.width > paper.height;
    const swap = wantsLandscape !== isLandscape;
    setPaper({
      orientation: o,
      width: swap ? paper.height : paper.width,
      height: swap ? paper.width  : paper.height,
    });
  };

  const presetLabel = (p: PaperPreset): string => {
    if (p === "CUSTOM") return t("preset.custom");
    if (p === "LETTER") return t("paper.letter");
    if (p === "LEGAL")  return t("paper.legal");
    const [w, h] = PAPER_PRESETS[p];
    return `${p} — ${w} × ${h} ${t("unit.mm")}`;
  };

  return (
    <Section title={t("section.paper")}>
      <div className="row">
        <label>{t("field.preset")}</label>
        <select
          className="ctl"
          value={paper.preset}
          onChange={(e) => onPreset(e.target.value as PaperPreset)}
        >
          {ALL_PRESETS.map((p) => (
            <option key={p} value={p}>{presetLabel(p)}</option>
          ))}
        </select>
      </div>
      <div className="row two">
        <div>
          <label>{t("field.width")}</label>
          <NumberInput
            value={paper.width}
            onChange={(width) => setPaper({ preset: "CUSTOM", width })}
            min={10} max={2000} step={0.1}
            unit={t("unit.mm")}
          />
        </div>
        <div>
          <label>{t("field.height")}</label>
          <NumberInput
            value={paper.height}
            onChange={(height) => setPaper({ preset: "CUSTOM", height })}
            min={10} max={2000} step={0.1}
            unit={t("unit.mm")}
          />
        </div>
      </div>
      <div className="row">
        <label>{t("field.orientation")}</label>
        <Segmented<Orientation>
          small
          value={paper.orientation}
          onChange={setOrientation}
          options={[
            { value: "auto", label: t("orient.auto") },
            { value: "portrait", label: t("orient.portrait") },
            { value: "landscape", label: t("orient.landscape") },
          ]}
        />
      </div>
    </Section>
  );
};
